import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Pagination,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { FaFilePdf, FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import {
  obterCorretoresComissaoImoveis,
  obterHistoricoComissaoImoveis,
  listarGruposEmpresaAdmin,
} from '~/Services/Service';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro, formatarMonetario } from '~/Utils/Helpers';

// W-0840 — ModalHistorico (Gate 3 Q-02=A — MVP)
// Lista paginada de comissões já geradas para "2ª via" sem reprocessar.
// LICOES §1.4: edge cases (delete último item, criar item, filtrar 1, clear filter).
// LICOES §1.5: keys estáveis (processamento_id).
// LICOES §1.7: sort por colunas Data e Valor desde V1.
const PAGE_SIZE = 10;
const SORT_OPTIONS = [
  { value: 'data_desc', label: 'Data (mais recente)' },
  { value: 'data_asc', label: 'Data (mais antiga)' },
  { value: 'valor_desc', label: 'Valor (maior)' },
  { value: 'valor_asc', label: 'Valor (menor)' },
];

const construirItensPaginacao = (paginaAtual, totalPaginas, mudarPagina) => {
  if (totalPaginas <= 1) return [];
  const itens = [];
  const maxBotoes = 5;

  itens.push(
    <Pagination.First
      key="first"
      onClick={() => mudarPagina(1)}
      disabled={paginaAtual === 1}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      onClick={() => mudarPagina(paginaAtual - 1)}
      disabled={paginaAtual === 1}
    />
  );

  let inicio = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
  if (fim - inicio < maxBotoes - 1) {
    inicio = Math.max(1, fim - maxBotoes + 1);
  }

  if (inicio > 1) {
    itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
  }
  for (let n = inicio; n <= fim; n += 1) {
    itens.push(
      <Pagination.Item
        key={`pag-${n}`}
        active={n === paginaAtual}
        onClick={() => mudarPagina(n)}
      >
        {n}
      </Pagination.Item>
    );
  }
  if (fim < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
  }
  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => mudarPagina(paginaAtual + 1)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => mudarPagina(totalPaginas)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  return itens;
};

const ModalHistorico = ({ aberto, onFechar, onBaixarPdf, baixandoPdf }) => {
  const [gesElegiveis, setGesElegiveis] = useState([]);
  const [corretoresElegiveis, setCorretoresElegiveis] = useState([]);
  const [filtros, setFiltros] = useState({
    codigoGrupoEmpresa: '',
    codigoConta: '',
    dataInicial: '',
    dataFinal: '',
    sort: 'data_desc',
  });
  const [pagina, setPagina] = useState(1);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);

  // Carrega GEs quando modal abre
  useEffect(() => {
    if (!aberto) return undefined;
    let ativo = true;
    listarGruposEmpresaAdmin({})
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.itens) || [];
        setGesElegiveis(lista);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, [aberto]);

  // Recarrega corretores quando GE muda (anti-race ciclo 9 L3)
  useEffect(() => {
    if (!aberto) return undefined;
    if (!filtros.codigoGrupoEmpresa) {
      setCorretoresElegiveis([]);
      return undefined;
    }
    let ativo = true;
    obterCorretoresComissaoImoveis(filtros.codigoGrupoEmpresa)
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.itens) || [];
        setCorretoresElegiveis(lista);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, [aberto, filtros.codigoGrupoEmpresa]);

  const carregarHistorico = useCallback(
    (paginaAlvo) => {
      let ativo = true;
      // MEMORY.md Shared Decisions §BE→FE: backend MVP ordena fixo `data DESC` e não aceita `sort=`.
      // Sort do usuário é aplicado client-side sobre a página atual.
      const queryParams = {
        ge: filtros.codigoGrupoEmpresa || undefined,
        corretor: filtros.codigoConta || undefined,
        dataInicial: filtros.dataInicial || undefined,
        dataFinal: filtros.dataFinal || undefined,
        page: paginaAlvo,
        pageSize: PAGE_SIZE,
      };
      setCarregando(true);
      obterHistoricoComissaoImoveis(queryParams)
        .then((res) => {
          if (!ativo) return;
          const lista = (res && res.itens) || [];
          const totalRetornado = (res && res.total) || lista.length;
          setItens(lista);
          setTotal(totalRetornado);
          // LICOES §1.4 — se a página atual ficou vazia após delete/filtro, retornar à anterior
          const totalPaginasNovo =
            Math.max(1, Math.ceil(totalRetornado / PAGE_SIZE));
          if (paginaAlvo > totalPaginasNovo) {
            setPagina(totalPaginasNovo);
          }
        })
        .catch((err) => {
          if (!ativo) return;
          toast.error(extrairMensagemErro(err));
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
      return () => {
        ativo = false;
      };
    },
    [
      filtros.codigoGrupoEmpresa,
      filtros.codigoConta,
      filtros.dataInicial,
      filtros.dataFinal,
    ]
  );

  useEffect(() => {
    if (!aberto) return undefined;
    const cleanup = carregarHistorico(pagina);
    return cleanup;
  }, [aberto, pagina, carregarHistorico]);

  const limparFiltros = () => {
    setFiltros({
      codigoGrupoEmpresa: '',
      codigoConta: '',
      dataInicial: '',
      dataFinal: '',
      sort: 'data_desc',
    });
    setPagina(1);
  };

  const aplicarFiltro = (campo, valor) => {
    setFiltros((prev) => {
      const proximo = { ...prev, [campo]: valor };
      if (campo === 'codigoGrupoEmpresa') proximo.codigoConta = '';
      return proximo;
    });
    setPagina(1);
  };

  const opcoesGE = gesElegiveis.map((ge) => ({
    value: ge.codigo_grupo_empresa,
    label: ge.apelido || ge.descricao || `GE ${ge.codigo_grupo_empresa}`,
  }));

  const opcoesCorretor = corretoresElegiveis.map((c) => ({
    value: c.codigo_conta,
    label: c.apelido ? `${c.apelido} — ${c.nome}` : c.nome,
  }));

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const inicioRegistro = total === 0 ? 0 : (pagina - 1) * PAGE_SIZE + 1;
  const fimRegistro = Math.min(pagina * PAGE_SIZE, total);

  // Sort client-side sobre a página atual (backend ordena fixo `data DESC` no MVP — Shared Decisions).
  // LICOES §1.7: sort por colunas Data e Valor desde V1.
  const itensOrdenados = [...itens].sort((a, b) => {
    if (filtros.sort === 'data_desc' || filtros.sort === 'data_asc') {
      const da = a.data ? moment(a.data).valueOf() : 0;
      const db = b.data ? moment(b.data).valueOf() : 0;
      return filtros.sort === 'data_desc' ? db - da : da - db;
    }
    if (filtros.sort === 'valor_desc' || filtros.sort === 'valor_asc') {
      const va = Number(a.valor_total) || 0;
      const vb = Number(b.valor_total) || 0;
      return filtros.sort === 'valor_desc' ? vb - va : va - vb;
    }
    return 0;
  });

  return (
    <Modal show={aberto} onHide={onFechar} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Histórico de Comissões — 2ª via</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12} md={6} lg={3} className="mb-2">
            <Form.Label className="small mb-1">Grupo de Empresa</Form.Label>
            <Select
              inputId="historicoGE"
              options={opcoesGE}
              isClearable
              placeholder="Todos"
              styles={customStyles}
              components={{ DropdownIndicator }}
              value={
                opcoesGE.find((o) => o.value === filtros.codigoGrupoEmpresa) ||
                null
              }
              onChange={(option) =>
                aplicarFiltro(
                  'codigoGrupoEmpresa',
                  option ? option.value : ''
                )
              }
            />
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-2">
            <Form.Label className="small mb-1">Corretor</Form.Label>
            <Select
              inputId="historicoCorretor"
              options={opcoesCorretor}
              isClearable
              isDisabled={!filtros.codigoGrupoEmpresa}
              placeholder={
                filtros.codigoGrupoEmpresa ? 'Todos' : 'Selecione um GE'
              }
              styles={customStyles}
              components={{ DropdownIndicator }}
              value={
                opcoesCorretor.find((o) => o.value === filtros.codigoConta) ||
                null
              }
              onChange={(option) =>
                aplicarFiltro('codigoConta', option ? option.value : '')
              }
            />
          </Col>
          <Col xs={6} md={3} lg={2} className="mb-2">
            <Form.Label className="small mb-1">Data Inicial</Form.Label>
            <Form.Control
              type="date"
              value={filtros.dataInicial}
              onChange={(e) => aplicarFiltro('dataInicial', e.target.value)}
            />
          </Col>
          <Col xs={6} md={3} lg={2} className="mb-2">
            <Form.Label className="small mb-1">Data Final</Form.Label>
            <Form.Control
              type="date"
              value={filtros.dataFinal}
              onChange={(e) => aplicarFiltro('dataFinal', e.target.value)}
            />
          </Col>
          <Col xs={12} lg={2} className="mb-2">
            <Form.Label className="small mb-1">Ordenar por</Form.Label>
            <Select
              inputId="historicoSort"
              options={SORT_OPTIONS}
              styles={customStyles}
              components={{ DropdownIndicator }}
              value={
                SORT_OPTIONS.find((o) => o.value === filtros.sort) ||
                SORT_OPTIONS[0]
              }
              onChange={(option) => aplicarFiltro('sort', option.value)}
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-end mb-2">
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={limparFiltros}
          >
            <FaTimes className="mr-1" /> Limpar filtros
          </Button>
        </div>

        {carregando ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted small mb-0">
              Carregando histórico…
            </p>
          </div>
        ) : itens.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FaSearch className="mb-2" size={32} />
            <p className="mb-0">
              Nenhuma comissão encontrada para os filtros aplicados.
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover table-sm mb-0">
              <thead className="thead-light">
                <tr>
                  <th>GE</th>
                  <th>Corretor</th>
                  <th>Data</th>
                  <th className="text-right">Valor Total</th>
                  <th className="text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {itensOrdenados.map((item) => (
                  <tr
                    key={
                      item.processamento_id ||
                      `${item.codigo_grupo_empresa}-${item.codigo_conta}-${item.data}`
                    }
                  >
                    <td>
                      {item.apelido_grupo_empresa ||
                        item.codigo_grupo_empresa}
                    </td>
                    <td>{item.nome_corretor || item.codigo_conta}</td>
                    <td>
                      {item.data
                        ? moment(item.data).format('DD/MM/YYYY')
                        : '-'}
                    </td>
                    <td className="text-right">
                      {formatarMonetario(item.valor_total, false)}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={baixandoPdf}
                        onClick={() => onBaixarPdf(item)}
                      >
                        <FaFilePdf className="mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
            <div className="text-muted small">
              Mostrando {inicioRegistro} até {fimRegistro} de {total}{' '}
              registros
            </div>
            <Pagination className="mb-0">
              {construirItensPaginacao(pagina, totalPaginas, setPagina)}
            </Pagination>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalHistorico;
